import React, { useEffect, useState } from 'react';
import { SCORE_RANGES } from './scoreCopy';
import { useAnimatedNumber } from '../calculator/useAnimatedNumber';

const CX = 160, CY = 160, R = 120;
const toAngle = (score) => Math.PI * (1 - (score - 300) / 600);
const pt = (a, r = R) => [CX + r * Math.cos(a), CY - r * Math.sin(a)];
const arc = (from, to) => {
  const [x1, y1] = pt(toAngle(from));
  const [x2, y2] = pt(toAngle(to));
  return `M ${x1} ${y1} A ${R} ${R} 0 0 1 ${x2} ${y2}`;
};

// Built as a string: JSX children inside <svg> get wrapped by the editor tooling and stop rendering
const gaugeSvg = (sel, shown, nx, ny) => [
  ...SCORE_RANGES.map((r) => `<path d="${arc(r.min + 2, r.max - 2)}" stroke="${r.color}" stroke-width="${sel.id === r.id ? 30 : 22}" stroke-linecap="round" fill="none" opacity="${sel.id === r.id ? 1 : 0.35}" style="cursor:pointer;transition:stroke-width .3s,opacity .3s" data-zone="${r.id}" data-testid="gauge-zone-${r.id}"></path>`),
  `<line x1="${CX}" y1="${CY}" x2="${nx}" y2="${ny}" stroke="#0f172a" stroke-width="5" stroke-linecap="round"></line>`,
  `<circle cx="${CX}" cy="${CY}" r="10" fill="#0f172a"></circle>`,
  '<text x="28" y="180" fill="#94a3b8" style="font-size:12px">300</text>',
  '<text x="292" y="180" text-anchor="end" fill="#94a3b8" style="font-size:12px">900</text>',
].join('');

export const ScoreGauge = ({ c }) => {
  const [sel, setSel] = useState(SCORE_RANGES[1]);
  const [auto, setAuto] = useState(true);
  useEffect(() => {
    if (!auto) return undefined;
    const id = setInterval(() => setSel((s) => SCORE_RANGES[(SCORE_RANGES.indexOf(s) + 1) % SCORE_RANGES.length]), 2800);
    return () => clearInterval(id);
  }, [auto]);
  const pick = (r) => { setAuto(false); setSel(r); };
  const mid = Math.round((sel.min + sel.max) / 2);
  const shown = useAnimatedNumber(mid, 900);
  const [nx, ny] = pt(toAngle(shown), R - 30);
  const [label, text] = c.ranges[sel.id];
  return (
    <div className="bg-white rounded-3xl border border-emerald-100 border-t-4 border-t-[#1a5c3b] shadow-[0_30px_70px_rgba(26,92,59,0.15)] p-6 md:p-8" data-testid="score-gauge">
      <p className="text-xl font-bold text-[#0f172a]">{c.gaugeTitle}</p>
      <p className="text-sm text-slate-500 mb-2">{c.gaugeHint}</p>
      <svg
        viewBox="0 0 320 185"
        className="w-full max-w-[380px] mx-auto"
        onClick={(e) => { const z = SCORE_RANGES.find((r) => r.id === e.target.dataset?.zone); if (z) pick(z); }}
        dangerouslySetInnerHTML={{ __html: gaugeSvg(sel, shown, nx, ny) }}
        data-testid="gauge-svg"
      />
      <p className="text-center text-5xl font-extrabold tabular-nums -mt-1 mb-3 transition-colors duration-500" style={{ color: sel.color }} data-testid="gauge-score">{Math.round(shown)}</p>
      <div className="flex flex-wrap justify-center gap-2 mt-2 mb-5">
        {SCORE_RANGES.map((r) => (
          <button key={r.id} type="button" onClick={() => pick(r)} className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-[background-color,border-color,color,transform] duration-300 ${sel.id === r.id ? 'text-white scale-105' : 'bg-white text-slate-600 border-gray-200 hover:border-gray-300'}`}
            style={sel.id === r.id ? { backgroundColor: r.color, borderColor: r.color } : undefined} data-testid={`gauge-chip-${r.id}`}>
            {c.ranges[r.id][0]} · {r.min}–{r.max}
          </button>
        ))}
      </div>
      <div className="rounded-2xl p-5 transition-colors duration-500 overflow-hidden" style={{ backgroundColor: `${sel.color}14` }} data-testid="gauge-explain">
        <div key={sel.id} className="animate-in fade-in slide-in-from-bottom-3 duration-500">
          <p className="font-bold text-lg mb-1" style={{ color: sel.color }}>{label} ({sel.min}–{sel.max})</p>
          <p className="text-slate-700 leading-relaxed">{text}</p>
        </div>
      </div>
      {auto && <div className="h-1 mt-4 rounded-full bg-gray-100 overflow-hidden"><div key={sel.id} className="h-full bg-[#1a5c3b]/40 animate-[gaugeProgress_2.8s_linear]" /></div>}
    </div>
  );
};
